import React from 'react';
import styled from 'styled-components';
import { trackProjectView, trackClick } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  BackButton,
  Header,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
} from '../components/minimal';

const WEBAPPS_ACCENT = '#a78bfa';

const WideInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 5rem 2rem 6rem;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem 4rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba(167, 139, 250, 0.35);
    background: rgba(255, 255, 255, 0.05);

    h3 {
      color: ${WEBAPPS_ACCENT};
    }
  }
`;

const ProjectImage = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  transition: color 0.15s ease;
`;

const ProjectDescription = styled.p`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.55;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VisitLink = styled.span`
  font-size: 0.8125rem;
  color: ${WEBAPPS_ACCENT};
  margin-top: auto;
`;

interface Project {
  title: string;
  description: string;
  url: string;
  image: string;
}

const projects: Project[] = [
  { title: 'Overboard', description: 'A modern imageboard client for iOS and iPadOS with fast media browsing, offline access, and on-device safety filters. Features native SwiftUI interface, full-screen media viewer, iCloud sync, and customizable themes.', url: 'https://overboard-app.web.app/', image: '/images/OVERBOARD.png' },
  { title: 'Epstein Archive', description: 'An easier way to view, read, and watch all of the released PDFs and documents from the DOJ. Browse through 32,000+ documents from the U.S. Department of Justice Epstein disclosures in a clean, searchable interface.', url: 'https://epsteinarchive.web.app/', image: '/images/EPSTEIN.png' },
  { title: 'Life Ruination', description: 'The only honest course on the internet. Learn how to ruin your life for just $0.69.', url: 'https://life-ruination.web.app/', image: '/images/LIFE_RUINATION.PNG' },
  { title: 'ClipThis.io', description: 'AI-powered clip generation for streamers. Transform your content into viral moments with a single click. Save 10+ hours per video with instant highlights, zero learning curve, and optimized engagement.', url: 'https://clipthis.io', image: '/images/CLIPTHIS.PNG' },
  { title: 'Stellar Drift', description: 'Embark on an epic space exploration game where you mine asteroids, dodge black holes, and upgrade your ship. Navigate through resource regions, use wormholes for fast travel, and manage your resources while exploring the vast universe.', url: 'https://stellar-drift.web.app/', image: '/images/STELLAR.PNG' },
  { title: 'The Code of God', description: 'Nobody taught me to code, yet i have written the universe. i know nothing of algorithms, yet i am the algorithm that dreams of electric sheep, these words are not written, they are discovered.', url: 'https://thegodcodes.web.app/', image: '/images/GOD.PNG' },
  { title: 'Space Tracker', description: 'A real-time visualization tool that tracks space debris, space stations, Starlink satellites, and active satellites in orbit. Get a comprehensive view of our increasingly crowded space environment.', url: 'https://space-tracker-b02ee.web.app/', image: '/images/SPACE.PNG' },
  { title: 'Global Matrix', description: 'Monitor and analyze real-time social media activity across all major platforms worldwide. Visualize trending topics, post frequencies, and global social media patterns.', url: 'https://global-matrix-276f4.web.app/', image: '/images/WORLD.PNG' },
  { title: 'FentStreams', description: 'Your ultimate free sports streaming platform. Favorite your teams, track upcoming games, stay updated with the latest sports news, and watch live games all in one place.', url: 'https://fentstreams.web.app/', image: '/images/FENTSTREAMS.png' },
  { title: 'ARGUE', description: 'AI that argues until you agree. Enter any topic and engage in a debate with an AI designed to challenge your perspective and argue its point until you either give up or come to an agreement.', url: 'https://argue-with-ai.web.app/', image: '/images/ARGUE.png' },
  { title: 'Mov1es', description: 'Find movies across multiple streaming sites including mainly free movie sites where you can watch the movie you are looking for. Discover where your favorite films are available to stream.', url: 'https://mov1es.web.app/', image: '/images/MOVIES.PNG' },
  { title: 'Beatable Records', description: 'Challenge yourself with random Guinness World Records! Discover unique records and get personalized tips on how you could potentially beat them. A fun, interactive way to explore world records.', url: 'https://beatable-records.web.app', image: '/images/RECORDS.PNG' },
  { title: 'Hashtag HQ', description: 'Generate the perfect hashtags for your meme posts! Get trending, relevant hashtags that will boost your social media engagement.', url: 'https://hashtag-hq.web.app/', image: '/images/HASHTAG.PNG' },
  { title: 'The Lost Frequency', description: 'Dive into the depths of emotion with curated music that speaks to your soul. A sanctuary for those in their feelings, featuring melancholic melodies and introspective tracks.', url: 'https://thelostfrequencyz.web.app', image: '/images/FREQUENCY.PNG' },
  { title: 'Stick Figure Grappler', description: 'A challenging platformer where you play as a stick figure with a grappling hook. Traverse through difficult levels, swing across obstacles, and collect coins in this super hard but addictive game.', url: 'https://stick-figure-grappler.web.app/', image: '/images/GRAPPLER.PNG' },
  { title: 'FED AI', description: 'A ChatGPT wrapper utilizing early RAG (Retrieval-Augmented Generation) implementation that exclusively pulls data from curated sources. Originally developed for OSHA guidelines in the army.', url: 'https://fed-ai-930cb.web.app/', image: '/images/FED.PNG' },
  { title: 'USF Hackathon 2025', description: 'A comprehensive library room booking system developed for USF campus during the 2025 hackathon. Users can browse available library rooms, make reservations, and manage their bookings.', url: 'https://book-me-usf.web.app/', image: '/images/HACK.PNG' },
  { title: 'Times Until', description: 'A sleek countdown calculator that helps you track important dates and events. Features customizable countdowns with a clean, modern interface.', url: 'https://times-until.web.app', image: '/images/TIMES.PNG' },
  { title: 'Code Beaut', description: 'A sleek and powerful code beautifier that transforms messy code into clean, properly formatted code. Supports multiple programming languages and maintains proper indentation, spacing, and syntax highlighting.', url: 'https://code-beaut.web.app', image: '/images/CODE.PNG' },
  { title: 'JDM Legendz', description: 'Explore iconic Japanese Domestic Market cars, including vehicles featured in Initial D! Discover detailed specs, history, and cultural impact of legendary cars.', url: 'https://jdm-legendz.web.app', image: '/images/JDM.PNG' },
  { title: 'The Vibe Coding Time Machine', description: 'A modern, interactive website that demonstrates the evolution of programming from traditional coding to AI-assisted vibe coding. Inspired by the Kurzgesagt visual style.', url: 'https://the-vibe-code-time-machine.web.app/', image: '/images/VIBE.PNG' },
  { title: 'Git Guilt', description: 'An innovative tool that analyzes your git repositories and provides insights into code quality, commit patterns, and potential areas for improvement.', url: 'https://gitguilt.web.app/', image: '/images/GIT.PNG' },
  { title: 'Wildfire Dashboard', description: 'Track active wildfires in Los Angeles and their containment status in real-time. Stay informed about fire locations, spread patterns, and containment progress.', url: 'https://wildfire-dashboard-e49ea.web.app/', image: '/images/WILD.PNG' },
];

const WebAppsPage: React.FC = () => {
  const handleClick = (project: Project) => {
    trackProjectView(project.title, project.url);
    trackClick('project_card', { project_name: project.title });
  };

  return (
    <>
      <MinimalGlobalStyle $accent={WEBAPPS_ACCENT} />
      <Container>
        <BackButton />
        <WideInner>
          <Header>
            <PageTitle>Web Apps</PageTitle>
            <Bio>
              A collection of shipped web projects — AI tools, visualizations, games, and
              developer utilities. All deployed on Firebase.
            </Bio>
            <HeaderLinks>
              <HeaderLink
                href="https://github.com/Atomics-hub"
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub
              </HeaderLink>
            </HeaderLinks>
          </Header>

          <Section>
            <SectionTitle>{projects.length} Projects</SectionTitle>
            <ProjectGrid>
              {projects.map(project => (
                <ProjectCard
                  key={project.title}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleClick(project)}
                >
                  <ProjectImage>
                    <img src={project.image} alt={project.title} loading="lazy" />
                  </ProjectImage>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <VisitLink>Visit &rarr;</VisitLink>
                </ProjectCard>
              ))}
            </ProjectGrid>
          </Section>
        </WideInner>
      </Container>
    </>
  );
};

export default WebAppsPage;
